import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, getChartColors, formatPercent } from './chartTheme';
import { useTheme } from '../../../stores/ThemeContext';
/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [symbol] - Trading symbol
 * @property {string} [direction] - Trade direction
 */

/**
 * @typedef {Object} DonutDataPoint
 * @property {string} result - Win/Loss/Breakeven
 * @property {number} count - Number of trades
 * @property {number} percentage - Percentage of total
 * @property {string} fill - Color for the segment
 */

/**
 * Breakeven threshold for categorizing trades
 * Trades within this range of 0 are considered breakeven
 */
const BREAKEVEN_THRESHOLD = 0.01;

/**
 * Win/Loss Distribution Doughnut Chart
 * Groups trades into Win/Loss/Breakeven and displays percentage and count
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects with profit property
 * @param {string} [props.title='Win/Loss Distribution'] - Chart title
 * @param {number} [props.height=300] - Chart height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 *
 * @example
 * <WinLossDonutChart
 *   trades={trades}
 *   title="Win Rate Distribution"
 *   height={350}
 * />
 */
const WinLossDonutChart = ({
  trades = [],
  title = 'Win/Loss Distribution',
  height = 300,
  className = ''
}) => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  // Handle responsive resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Compute chart data from trades
  const chartData = useMemo(() => {
    if (!trades || trades.length === 0) {
      return [];
    }

    let wins = 0;
    let losses = 0;
    let breakeven = 0;

    trades.forEach((trade) => {
      const profit = Number(trade.profit) || 0;
      if (profit > BREAKEVEN_THRESHOLD) {
        wins++;
      } else if (profit < -BREAKEVEN_THRESHOLD) {
        losses++;
      } else {
        breakeven++;
      }
    });

    const total = trades.length;

    /** @type {DonutDataPoint[]} */
    const data = [];

    if (wins > 0) {
      data.push({
        result: 'Wins',
        count: wins,
        percentage: (wins / total) * 100,
        fill: CHART_COLORS.win,
      });
    }

    if (losses > 0) {
      data.push({
        result: 'Losses',
        count: losses,
        percentage: (losses / total) * 100,
        fill: CHART_COLORS.loss,
      });
    }

    if (breakeven > 0) {
      data.push({
        result: 'Breakeven',
        count: breakeven,
        percentage: (breakeven / total) * 100,
        fill: CHART_COLORS.breakeven,
      });
    }

    return data;
  }, [trades]);

  // Calculate summary stats for center display
  const summaryStats = useMemo(() => {
    const total = trades?.length || 0;
    const wins = chartData.find(d => d.result === 'Wins')?.count || 0;
    const winRate = total > 0 ? (wins / total) * 100 : 0;
    return { total, wins, winRate };
  }, [trades, chartData]);

  // Chart options - using 'pie' with innerRadiusRatio for donut effect (donut is Enterprise-only)
  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    data: chartData,
    title: {
      text: title,
      fontSize: 16,
      fontWeight: 600,
      color: chartColors.text,
    },
    series: [
      {
        type: 'pie',
        angleKey: 'count',
        legendItemKey: 'result',
        fills: chartData.map(d => d.fill),
        strokes: chartData.map(d => d.fill),
        strokeWidth: 0,
        innerRadiusRatio: 0.6,
        calloutLabel: {
          enabled: true,
        },
        sectorLabel: {
          enabled: true,
          formatter: ({ datum }) => formatPercent(datum.percentage),
          color: chartColors.text,
          fontWeight: 600,
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.result,
            content: `Count: ${datum.count} (${formatPercent(datum.percentage)})`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 0.8,
            strokeWidth: 2,
            stroke: chartColors.text,
          },
        },
      },
    ],
    legend: {
      enabled: true,
      position: 'bottom',
      item: {
        marker: {
          shape: 'circle',
          size: 10,
        },
        label: {
          color: chartColors.text,
          fontSize: 12,
        },
        paddingX: 16,
        paddingY: 8,
      },
    },
    background: {
      fill: CHART_COLORS.background,
    },
  }), [chartData, title, isDark]);

  // Empty state
  if (!trades || trades.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No trade data</p>
          <p className="text-sm">Add trades to see win/loss distribution</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
      <AgCharts ref={chartRef} options={options} />
    </div>
  );
};

export default WinLossDonutChart;
